import type { TournamentMatchView } from "./types";
import { tournamentModeLabel } from "./types";

function playerLabel(p: TournamentMatchView["player1"], empty = "TBD"): string {
  if (!p) return empty;
  return p.displayName || p.userId.slice(0, 8);
}

function matchStatusClass(status: string): string {
  if (status === "completed" || status === "forfeit") return "tournamentBracketSlot--done";
  if (status === "active" || status === "waiting" || status === "open") return "tournamentBracketSlot--live";
  if (status === "tie_pending") return "tournamentBracketSlot--tie";
  return "";
}

function matchDotsLabel(
  round: number,
  isFinal: boolean,
  roundRewards: Record<string, number>,
): string | null {
  const roundOnly = Math.max(0, Math.floor(Number(roundRewards[String(round)]) || 0));
  const winDots = isFinal ? Math.max(0, Math.floor(Number(roundRewards.win) || 0)) : 0;
  if (isFinal) {
    if (roundOnly > 0 && winDots > 0) {
      return `2nd +${roundOnly.toLocaleString()} · Champ +${winDots.toLocaleString()}`;
    }
    if (winDots > 0) return `Champ +${winDots.toLocaleString()} Dots`;
    if (roundOnly > 0) return `2nd +${roundOnly.toLocaleString()} Dots`;
    return null;
  }
  if (roundOnly > 0) return `+${roundOnly.toLocaleString()} Dots`;
  return null;
}

export function renderBracketGrid(
  bracketSize: number,
  matches: TournamentMatchView[],
  viewerUserId: string | null,
  roundRewards: Record<string, number> = {},
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "tournamentBracketWrap";
  const rounds = Math.log2(bracketSize);
  let start = 0;
  let count = bracketSize / 2;
  for (let r = 1; r <= rounds; r++) {
    const col = document.createElement("div");
    col.className = "tournamentBracketRound";
    col.dataset.round = String(r);
    const title = document.createElement("p");
    title.className = "tournamentBracketRoundTitle";
    title.textContent = r === rounds ? "FINAL" : `ROUND ${r}`;
    col.appendChild(title);
    const isFinal = r === rounds;
    for (let i = 0; i < count; i++) {
      const matchIndex = start + i;
      const m = matches.find((x) => x.matchIndex === matchIndex);
      const slot = document.createElement("div");
      slot.className = `tournamentBracketSlot ${m ? matchStatusClass(m.status) : ""}`;

      const vs = document.createElement("p");
      vs.className = "tournamentBracketVs";
      const p1Name = m ? playerLabel(m.player1) : "TBD";
      const p2Name = m ? playerLabel(m.player2) : "TBD";
      vs.textContent = `${p1Name}  vs  ${p2Name}`;

      const mode = document.createElement("p");
      mode.className = "tournamentBracketMode";
      mode.textContent = m ? `Mode: ${tournamentModeLabel(m.modeId)}` : "Mode: —";

      slot.append(vs, mode);

      const dotsLabel = matchDotsLabel(m?.round ?? r, isFinal, roundRewards);
      if (dotsLabel) {
        const dots = document.createElement("p");
        dots.className = "tournamentBracketDots";
        dots.textContent = dotsLabel;
        slot.appendChild(dots);
      }

      if (m) {
        if (m.winnerUserId === m.player1?.userId) vs.classList.add("tournamentBracketVs--p1win");
        if (m.winnerUserId === m.player2?.userId) vs.classList.add("tournamentBracketVs--p2win");
        const isViewer =
          viewerUserId &&
          (m.player1?.userId === viewerUserId || m.player2?.userId === viewerUserId);
        if (isViewer) slot.classList.add("tournamentBracketSlot--yours");

        if (m.player1?.finished || m.player2?.finished) {
          const scores = document.createElement("p");
          scores.className = "tournamentBracketScores";
          const s1 = m.player1?.score != null ? String(m.player1.score) : "—";
          const s2 = m.player2?.score != null ? String(m.player2.score) : "—";
          scores.textContent = `${s1} — ${s2}`;
          slot.appendChild(scores);
        }
      }

      col.appendChild(slot);
    }
    wrap.appendChild(col);
    start += count;
    count /= 2;
  }
  return wrap;
}

export function renderAdminBracketPreview(
  bracketSize: number,
  matchModes: Array<{ matchIndex: number; modeId: string }>,
  onModeChange: (matchIndex: number, modeId: string) => void,
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "tournamentBracketWrap tournamentBracketWrap--admin";
  const rounds = Math.log2(bracketSize);
  let start = 0;
  let count = bracketSize / 2;
  for (let r = 1; r <= rounds; r++) {
    const col = document.createElement("div");
    col.className = "tournamentBracketRound";
    for (let i = 0; i < count; i++) {
      const matchIndex = start + i;
      const cfg = matchModes[matchIndex] ?? { matchIndex, modeId: "normal" };
      const slot = document.createElement("div");
      slot.className = "tournamentBracketSlot tournamentBracketSlot--preview";
      const label = document.createElement("p");
      label.className = "tournamentBracketMode";
      label.textContent = `Match ${matchIndex + 1}`;
      const sel = document.createElement("select");
      sel.className = "formInput tournamentBracketModeSelect";
      for (const modeId of [
        "normal",
        "wasd",
        "superdot",
        "roulette",
        "sandbox",
        "highway",
        "darkness",
        "mirror",
        "snake",
        "combo",
        "colorrush",
        "heat",
        "hunter",
        "pop",
        "counter",
        "aim",
        "hook",
        "tilt",
        "orbit",
        "delay",
        "progression",
      ]) {
        const opt = document.createElement("option");
        opt.value = modeId;
        opt.textContent = modeId.toUpperCase();
        if (cfg.modeId === modeId) opt.selected = true;
        sel.appendChild(opt);
      }
      sel.onchange = () => onModeChange(matchIndex, sel.value);
      const empty1 = document.createElement("p");
      empty1.className = "tournamentBracketPlayer";
      empty1.textContent = "—";
      const empty2 = document.createElement("p");
      empty2.className = "tournamentBracketPlayer";
      empty2.textContent = "—";
      slot.append(label, sel, empty1, empty2);
      col.appendChild(slot);
    }
    wrap.appendChild(col);
    start += count;
    count /= 2;
  }
  return wrap;
}
